// 2) Third-party library imports
import { fetchAuthSession } from '@aws-amplify/auth';
import { get, map, orderBy } from 'lodash-es';
import { all, call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';

// 3a) Local imports � Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from '../../config/config';
import { DETAILS_TAB, PAYMENT_PLANS_PAGE } from '../../config/tableAndPageConstants';
import { FamilyNameAttribute, GivenNameAttribute } from '../../constants/authUserAttributes';
import { fetchPaymentPlanScheduleHandler } from '../../constants/paymentPlansSortAndFilters';

// 3c) Local imports � Project-specific modules / types
import { _queries as queries } from '../../graphql/queries.graphql';
import { PageData } from '../common/types';
import {
    PaymentPlan,
    PaymentPlan as PaymentPlanType} from './types';

// 3d) Local imports � Utility functions / interfaces
import dayjs from 'src/config/dayjs';
import {
    checkShouldRequestRefetch,
    customPostData,
    formatDateToDateObjectUTC,
    removeAppliedFiltersForApiRequest,
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import client, { delApi, post } from 'src/api';

// 3e) Local imports � Redux actions & selectors
import { getCurrentUser } from '../auth/authSlice';
import { getCloudImportType } from '../companies/companySlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import {
    fetchPaymentPlanCommunicationDetail,
    getPaymentPlanConversationErrorAction,
    getPaymentPlanConversationSuccessAction,
    getPaymentPlanDataErrorAction,
    getPaymentPlanDataScheduleErrorAction,
    getPaymentPlanDataScheduleSuccessAction,
    getPaymentPlanDataSuccessAction,
    getPaymentPlanInvoicesErrorAction,
    getPaymentPlanInvoicesSuccessAction,
    getPaymentPlansErrorAction,
    getPaymentPlansRequestAction,
    getPaymentPlansSuccessAction,
    setPaymentPlanSelectedIdSuccessAction,
    getPaymentPlanConfigSuccessAction,
    getPaymentPlanConfigErrorAction,
    createPaymentPlanConfigSuccessAction,
    createPaymentPlanConfigErrorAction,
    createPaymentPlanConfigRequestAction,
    deletePaymentPlanConfigsRequestAction,
    deletePaymentPlanConfigsSuccessAction,
    deletePaymentPlanConfigsErrorAction,
    getPaymentPlanConfigRequestAction,
    updatePaymenPlanScheduleRequestAction,
    manualCreateNewPaymentPlanRequestAction,
    resendPaymentPlanApprovalRequestAction,
    cancelPaymentPlanRequestAction,
    checkPaymentPlansActionStateAction,
    actionOrganisationPaymentPlanTasks,
    actionPaymentPlanTasks,
    actionOrganisationPaymentPlanRequestAction,
    actionPaymentPlanRequestAction,
    requestPaymentPlansAction,
    createPaymentPlanAction,
    paymentPlanAddCommentRequestAction,
    getPaymentPlanConversationRequestAction,
    getPaymentPlanDataScheduleRequestAction,
    getPaymentPlanInvoicesRequestAction,
    setPaymentPlanSelectedIdRequestAction,
    getPaymentPlanDataRequestAction,
    getPaymentPlanData,
    getPaymentPlansPageData,
    getNumberOfInstalmentsRequestAction,
} from './paymentPlanSlice';

let refetchCount = 0;
/**
 * Function that calls the API for fetching the paymentPlan list.
 * @param param0
 */
function* handleGetPaymentPlansRequest({ payload }: any) {
    const errorMessage =
        'Error fetching payment plans list. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            paymentPlanState,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'paymentPlans'
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENT_PLANS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    PaymentPlanState: paymentPlanState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * PAYMENT_PLANS_PAGE.pageSize
                }
            }
        );

        const { PaymentPlans } = get(res.data, 'GetPaymentPlansForCompany');

        const responsePayload = {
            data: PaymentPlans,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(PaymentPlans.length < pageSize) &&
                    !(pageSize < PAYMENT_PLANS_PAGE.pageSize),
            },
        };

        refetchCount = 0;

        yield put(getPaymentPlansSuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getPaymentPlansRequestAction(payload));
        } else {
            yield put(getPaymentPlansErrorAction([errorMessage]));
        }
    }
}

/**
 * Function calling the API for fetching the paymentPlan data based on the given id.
 * @param param0
 */
function* handleGetPaymentPlanDataRequest({
    payload: { paymentPlanId, isUsingCloudImportType },
}: any) {
    const errorMessage = "Error fetching payment plan details. Please try again later.";

    try {
        const cloudImportType: string = yield select(getCloudImportType);
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENT_PLAN_DETAILS_FOR_COMPANY,
                variables: {
                    PaymentPlanId: paymentPlanId,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            }
        );

        const PaymentPlan = get(res.data, 'GetPaymentPlanDetailsForCompany');

        if (PaymentPlan) {
            const responsePayload = {
                record: PaymentPlan,
            };

            yield put(getPaymentPlanDataSuccessAction(responsePayload));
        } else {
            yield put(getPaymentPlanDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentPlanDataErrorAction([errorMessage]));
    }
}

/**
 * Function that sets the selected paymentPlanId id for reference.
 * @param param0
 */
function* handleSetPaymentPlanSelectedIdRequest({ payload }: any) {
    const { paymentPlanId, callback } = payload;

    yield put(setPaymentPlanSelectedIdSuccessAction(paymentPlanId));

    if (callback) {
        callback();
    }
}

/**
 * Function that gets the invoices list for a certain paymentPlan.
 * @param param0
 */
function* handleGetPaymentPlanDataInvoicesRequest({ payload }: any) {
    const errorMessage = "Error fetching payment plan's invoice list. Please try again later.";

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                }
            },
        );

        const { Invoices } = get(res.data, 'GetInvoicesForCompany');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Invoices.length < pageSize),
                },
            };

            yield put(getPaymentPlanInvoicesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentPlanInvoicesErrorAction([errorMessage]));
    }
}

/**
 * Payment Plan Data Schedule
 */
/**
 * Function that fetches the schedule for the selected payment plan.
 * @param param0
 */
function* handleGetPaymentPlanDataScheduleRequest({ payload }: any) {
    const errorMessage = `Error fetching payment plan's schedule list. Please try again later.`;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        
        const paymentPlanActiveData: PaymentPlanType = yield select(
            getPaymentPlanData
        );
        const paymentPlan = get(paymentPlanActiveData, 'record');
        const paymentPlanSchedules = get(
            paymentPlan,
            'PaymentPlanSchedules',
            []
        );
        
        const planSchedulesFiltered: any[] = fetchPaymentPlanScheduleHandler(
            paymentPlanSchedules,
            cleanFilters
        );

        if (paymentPlanSchedules) {
            const sortOrder = sortAscending ? 'asc' : 'desc';
            const paymentPlanSorted = orderBy(
                planSchedulesFiltered,
                [sortBy],
                [sortOrder]
            );

            const responsePayload = {
                data: paymentPlanSorted,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(paymentPlanSchedules.length < pageSize) &&
                        !(pageSize < DETAILS_TAB.INVOICE_LIST.pageSize),
                },
            };

            yield put(getPaymentPlanDataScheduleSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentPlanDataScheduleErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given paymentPlan.
 * @param param0
 */
function* handleGetPaymentPlanConversationRequest({ payload }: any) {
    const errorMessage = "Error fetching paymentPlan's conversation list. Please try again later.";

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONVERSATION_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                }
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForCompany'
        );
        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getPaymentPlanConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentPlanConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for adding comment to the paymentPlan's conversation list.
 * @param param0
 */
function* handlePaymentPlanAddCommentRequest({ payload: sagaPayload }: any) { 
    const { filter, paymentPlanIds, excludePaymentPlans, comment, callback } =
        sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'paymentPlans'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        PaymentPlanManagementFilter: {
            ...cleanFilters,
            PaymentPlanIds: paymentPlanIds,
            ExcludePaymentPlans: excludePaymentPlans,
        },
        Comment: comment,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/conversation/save/paymentplancomment',
            options: {
                body: payload
            }
        });

        if (callback) {
            let RefetchList = true;

            if (paymentPlanIds.length === 1 && excludePaymentPlans === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const paymentPlansUpdated: DynamicObject[] = map(
                    paymentPlanIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(
                    getPaymentPlansPageData
                );

                yield put(
                    getPaymentPlansSuccessAction({
                        data: paymentPlansUpdated as PaymentPlan[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for creating paymentplan.
 * @param param0
 */
function* handleCreatePaymentPlan({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { Messages, IsAutoApproval, FeeAmount } = res;

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages,
                IsAutoApproval,
                FeeAmount
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for creating dummy paymentplan.
 * @param param0
 */
 function* handleAssignPaymentPlan({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/assignconfig',
            options: {
                body: payload
            }
        });
        
        const paymentPlans: PaymentPlan[] = yield body.json();

        if (callback) {
            callback({
                IsSuccess: true,
                PaymentPlans: paymentPlans || []
            });
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the data needed to populate the public Payment schedule details page from API.
 * @param param0
 */
function* handleFetchPaymentScheduleDetailsPublic({
    payload: sagaPayload,
}: any) {
    const { communicationId, applyDelay, callback } = sagaPayload;

    try {
        if (applyDelay) {
            // delay for 3 seconds
            yield delay(3000);
        }

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENT_PLAN_COMMUNICATION_DETAIL,
                variables: {
                    CommunicationId: communicationId || null,
                    AllInvoices: true,
                }
            }
        );

        res.IsSuccess = true;
        refetchCount = 0;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(
                fetchPaymentPlanCommunicationDetail(
                    communicationId,
                    applyDelay,
                    callback
                )
            );
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function called when actioning a payment plan task.
 * @param param0
 */
function* handleActionPaymentPlanRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/paymentplan',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Task has been actioned successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleActionOrganisationPaymentPlanRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    let apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/paymentplan`,
                payload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/paymentplan',
                options: {
                    body: payload,
                }
            });
        }
        
        const { TaskEditAction } = yield res.body.json();
        let successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Task has been actioned successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called when actioning a batch of multiple payment plan tasks.
 * @param param0
 */
function* handleActionPaymentPlansRequest({ payload: sagaPayload }: DynamicObject) {
    const { filter, taskIds, excludeTasks, callback, ...fields } = sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'paymentPlans'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        TaskManagementFilter: {
            ...cleanFilters,
            TaskIds: taskIds,
            ExcludeTasks: excludeTasks,
        },
        ...fields
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/paymentplans',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: string[] = [];

        if (TaskEditAction) {
            successMessages.push(`Tasks has been actioned successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleActionOrganisationPaymentPlansRequest({ payload: sagaPayload }: DynamicObject) {
    const { filter, taskIds, excludeTasks, callback, ...fields } = sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'paymentPlans'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        TaskManagementFilter: {
            ...cleanFilters,
            TaskIds: taskIds,
            ExcludeTasks: excludeTasks,
        },
        ...fields
    };

    let apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');
            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/paymentplans`,
                payload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/paymentplans',
                options: {
                    body: payload,
                }
            });
        }
        
        const { TaskEditAction } = yield res.body.json();
        let successMessages: string[] = [];

        if (TaskEditAction) {
            successMessages.push(`Tasks has been actioned successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for checking the state of payment plans selected to enable/disable the `Cancel` button in
 * Payment Plans page.
 * @param param0
 */
function* handleCheckPaymentPlansActionStateRequest({ payload }: any) {
    const {
        filters,
        sortBy,
        sortAscending,
        pageSize,
        currentPage,
        paymentPlanIds,
        excludePaymentPlans,
        callback,
    } = payload;

    // To call async functions, use redux-saga's `call()`.
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filters,
        true,
        'paymentPlans'
    );

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.CHECK_PAYMENT_PLANS_ACTION_STATE,
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    PaymentPlanIds: paymentPlanIds,
                    ExcludePaymentPlans: excludePaymentPlans,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * PAYMENT_PLANS_PAGE.pageSize,
                }
            }
        );

        const paymentPlansActionState = get(res.data, 'PaymentPlansActionState');

        if (callback) {
            callback(paymentPlansActionState);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when cancelling a payment plan task.
 * @param param0
 */
function* handleCancelPaymentPlanRequest({ payload: sagaPayload }: any) {
    const { filter, paymentPlanIds, excludePaymentPlans, comment, callback, notifyCustomerCancellation, selectedContact } = sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'paymentPlans'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        PaymentPlanManagementFilter: {
            ...cleanFilters,
            PaymentPlanIds: paymentPlanIds,
            ExcludePaymentPlans: excludePaymentPlans,
        },
        Comment: comment,
        NotifyCustomerCancellation: notifyCustomerCancellation,
        SelectedContact: selectedContact
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/cancel',
            options: {
                body: payload
            }
        });

        if (callback) {
            let RefetchList = true;

            if (paymentPlanIds.length === 1 && excludePaymentPlans === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const paymentPlansUpdated: DynamicObject[] = map(
                    paymentPlanIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(
                    getPaymentPlansPageData
                );

                yield put(
                    getPaymentPlansSuccessAction({
                        data: paymentPlansUpdated as PaymentPlan[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleResendPaymentPlanApprovalRequest({ payload: sagaPayload }: any) {
    const { filter, paymentPlanIds, excludePaymentPlans, selectedContact, callback } =
        sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'paymentPlans'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        PaymentPlanManagementFilter: {
            ...cleanFilters,
            PaymentPlanIds: paymentPlanIds,
            ExcludePaymentPlans: excludePaymentPlans,
        },
        SelectedContact: selectedContact
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/resend-approval',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called when action manual create payment plan tasks.
 * @param param0
 */
function* handleActionManualCreatePaymentPlansRequest({ payload: sagaPayload }: DynamicObject) {
    const { 
        InvoiceIds,
        InstalmentPayments,
        DepositPercentage,
        DepositAmount,
        DepositDueDate,
        callback,
    } = sagaPayload;

    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/manual/create',
            options: {
                body: {
                    InvoiceIds,
                    InstalmentPayments,
                    DepositPercentage,
                    DepositAmount,
                    DepositDueDate,
                    callback,
                }
            }
        });

        const paymentPlan: DynamicObject = yield res.body.json();
        const successMessages: any = [];

        if (paymentPlan) {
            successMessages.push(`The payment plan is created successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                PaymentPlan: paymentPlan,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;
            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for edit the paymentPlan schedule.
 * @param param0
 */
function* handleUpdatePaymentPlanScheduleRequest({ payload: sagaPayload }: DynamicObject) { 
    const { payload, callback } =
        sagaPayload;

    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/schedule/edit',
            options: {
                body: {
                    InstalmentPayments: payload
                }
            }
        });
       
        const successMessages: any = [];

        if (res) {
            successMessages.push(`The payment plan schedule is updated successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for fetching the conversation list of a given paymentPlan.
 * @param param0
 */
function* handleGetPaymentPlanConfig() {
    const errorMessage = "Error fetching paymentPlan's conversation list. Please try again later.";

    try {
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_PAYMENT_PLAN_CONFIG
            }
        );

        const paymentPlanConfigData = get(
            res.data,
            'GetPaymentPlanConfig.Configurations'
        );

        yield put(getPaymentPlanConfigSuccessAction(paymentPlanConfigData));
    } catch (err) {
        yield put(getPaymentPlanConfigErrorAction([errorMessage]));
        
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

/**
 * Function for creating payment plan config.
 * @param param0
 */
function* handleCreatePaymentPlanConfig({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    try { 
        yield call(post, {
            apiName: API_NAME, 
            path: '/paymentplan/save/paymentplan-config', 
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
            yield put(createPaymentPlanConfigSuccessAction());
        }
    } catch (err) {
        if (callback) {
            let returnData: DynamicObject;

            if (typeof err === 'object' && err !== null && 'response' in err) {
                const errorResponse = err as { response: { data: any } };

                returnData = get(errorResponse.response, 'data')
                    ? errorResponse.response.data
                    : { Messages: ['An error occurred'] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
            yield put(createPaymentPlanConfigErrorAction());
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for deleting payment plan configs.
 * @param param0
 */
function* handleDeletePaymentPlanConfigs({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    
    try { 
        yield call(delApi,
            '/paymentplan/delete/paymentplan-config', 
            {
                ...payload
            }
        );

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
            yield put(deletePaymentPlanConfigsSuccessAction());
        }
    } catch (err) {
        if (callback) {
            let returnData: DynamicObject;

            if (typeof err === 'object' && err !== null && 'response' in err) {
                const errorResponse = err as { response: { data: any } };

                returnData = get(errorResponse.response, 'data')
                    ? errorResponse.response.data
                    : { Messages: ['An error occurred'] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
            yield put(deletePaymentPlanConfigsErrorAction());
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting total number of payment plan instalments basen .
 * @param param0
 */
function* handleGetNumberOfInstalmentsRequest({ payload: sagaPayload }: any) {
    const { paymentPlanIds, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_NUMBER_OF_INSTALMENTS_FOR_PAYMENT_PLANS,
                variables: {
                    PaymentPlanIds: paymentPlanIds
                }
            }
        );

        const numberOfInstalments = get(
            res.data,
            'GetNumberOfInstalmentsForPaymentPlans'
        );

        callback(numberOfInstalments);
    } catch (err) {        
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetPaymentPlansRequest() {
    yield takeLatest(
        getPaymentPlansRequestAction.type,
        handleGetPaymentPlansRequest
    );
}

function* watchGetPaymentPlanDataRequest() {
    yield takeLatest(
        getPaymentPlanDataRequestAction.type,
        handleGetPaymentPlanDataRequest
    );
}

function* watchSetPaymentPlanSelectedIdRequest() {
    yield takeLatest(
        setPaymentPlanSelectedIdRequestAction.type,
        handleSetPaymentPlanSelectedIdRequest
    );
}

function* watchGetPaymentPlanDataInvoicesRequest() {
    yield takeLatest(
        getPaymentPlanInvoicesRequestAction.type,
        handleGetPaymentPlanDataInvoicesRequest
    );
}

function* watchGetPaymentPlanDataScheduleRequest() {
    yield takeLatest(
        getPaymentPlanDataScheduleRequestAction.type,
        handleGetPaymentPlanDataScheduleRequest
    );
}

function* watchGetPaymentPlanConversationRequest() {
    yield takeLatest(
        getPaymentPlanConversationRequestAction.type,
        handleGetPaymentPlanConversationRequest
    );
}

function* watchPaymentPlanAddCommentRequest() {
    yield takeLatest(
        paymentPlanAddCommentRequestAction.type,
        handlePaymentPlanAddCommentRequest
    );
}

function* watchCreatePaymentPlan() {
    yield takeLatest(
        createPaymentPlanAction.type,
        handleCreatePaymentPlan
    );
}

function* watchAssignPaymentPlan() {
    yield takeLatest(
        requestPaymentPlansAction.type,
        handleAssignPaymentPlan
    );
}

function* watchFetchPaymentScheduleDetailsPublic() {
    yield takeLatest(
        fetchPaymentPlanCommunicationDetail.type,
        handleFetchPaymentScheduleDetailsPublic
    );
}

function* watchActionPaymentPlanRequest() {
    yield takeLatest(
        actionPaymentPlanRequestAction.type,
        handleActionPaymentPlanRequest
    );
}

function* watchActionOrganisationPaymentPlanRequest() {
    yield takeLatest(
        actionOrganisationPaymentPlanRequestAction.type,
        handleActionOrganisationPaymentPlanRequest
    );
}

function* watchActionPaymentPlansRequest() {
    yield takeLatest(
        actionPaymentPlanTasks.type,
        handleActionPaymentPlansRequest
    );
}

function* watchActionOrganisationPaymentPlansRequest() {
    yield takeLatest(
        actionOrganisationPaymentPlanTasks.type,
        handleActionOrganisationPaymentPlansRequest
    );
}

function* watchCheckPaymentPlansActionStateRequest() {
    yield takeLatest(
        checkPaymentPlansActionStateAction.type,
        handleCheckPaymentPlansActionStateRequest
    );
}

function* watchCancelPaymentPlanRequest() {
    yield takeLatest(
        cancelPaymentPlanRequestAction.type,
        handleCancelPaymentPlanRequest
    );
}

function* watchResendPaymentPlanApprovalRequest() {
    yield takeLatest(
        resendPaymentPlanApprovalRequestAction.type,
        handleResendPaymentPlanApprovalRequest
    );
}

function* watchActionManualCreatePaymentPlanRequest() {
    yield takeLatest(
        manualCreateNewPaymentPlanRequestAction.type,
        handleActionManualCreatePaymentPlansRequest
    );
}

function* watchUpdatePaymentPlanScheduleRequest() {
    yield takeLatest(
        updatePaymenPlanScheduleRequestAction.type,
        handleUpdatePaymentPlanScheduleRequest
    );
}

function* watchGetPaymentPlanConfigRequest() {
    yield takeLatest(
        getPaymentPlanConfigRequestAction.type,
        handleGetPaymentPlanConfig
    )
}

function* watchCreatePaymentPlanConfigRequest() {
    yield takeLatest(
        createPaymentPlanConfigRequestAction.type,
        handleCreatePaymentPlanConfig
    );
}

function* watchDeletePaymentPlanConfigsRequest() {
    yield takeLatest(
        deletePaymentPlanConfigsRequestAction.type,
        handleDeletePaymentPlanConfigs
    );
}

function* watchGetNumberOfInstalmentsRequest() {
    yield takeLatest(
        getNumberOfInstalmentsRequestAction.type,
        handleGetNumberOfInstalmentsRequest
    );
}


// We can also use `fork()` here to split our saga into multiple watchers.
function* paymentPlansSaga() {
    yield all([
        fork(watchGetPaymentPlansRequest),
        fork(watchGetPaymentPlanDataRequest),
        fork(watchSetPaymentPlanSelectedIdRequest),
        fork(watchGetPaymentPlanDataInvoicesRequest),
        fork(watchGetPaymentPlanDataScheduleRequest),
        fork(watchGetPaymentPlanConversationRequest),
        fork(watchPaymentPlanAddCommentRequest),
        fork(watchCreatePaymentPlan),
        fork(watchFetchPaymentScheduleDetailsPublic),
        fork(watchActionPaymentPlanRequest),
        fork(watchActionOrganisationPaymentPlanRequest),
        fork(watchCancelPaymentPlanRequest),
        fork(watchResendPaymentPlanApprovalRequest),
        fork(watchCheckPaymentPlansActionStateRequest),
        fork(watchAssignPaymentPlan),
        fork(watchActionPaymentPlansRequest),
        fork(watchActionOrganisationPaymentPlansRequest),
        fork(watchActionManualCreatePaymentPlanRequest),
        fork(watchUpdatePaymentPlanScheduleRequest),
        fork(watchGetPaymentPlanConfigRequest),
        fork(watchCreatePaymentPlanConfigRequest),
        fork(watchDeletePaymentPlanConfigsRequest),
        fork(watchGetNumberOfInstalmentsRequest)
    ]);
}

export default paymentPlansSaga;